<template lang="html">
  <section>
    <div class="no-data" v-if="noData"><p>Your {{isOutbox ? 'outbox' : 'inbox'}} is empty</p></div>

    <div v-else>
      <header class="messages-header">
        <h1>{{isOutbox ? 'Sent Messages' : 'Messages'}}</h1>
        <div>
          <label for="sort-by">Order by:</label>
          <select id="sort-by" @change="updateSortParam" v-model="sortBy">
            <option value="" disabled selected>Select one</option>
            <option v-for="sort in sortOptions" :value="sort.direction">
              {{sort.label}}
            </option>
          </select>
        </div>
      </header>

      <ul class="messages">
        <li class="message" v-for="message in rows">
          <div class="recipient-container">
            <div class="recipient">
              <div>
                <div class="avatar">
                  <span class="initial">{{isOutbox ? recipientInitial(message) : senderInitial(message)}}</span>
                </div>
              </div>
              <div class="recipient-details">
                <p class="recipient-name">{{isOutbox ? recipientName(message) : senderName(message)}}</p>
              </div>
            </div>
            <div v-if="message.read_at == null" class="unread-label">UNREAD</div>
          </div>

          <div @click="{mob ? mobMessage(message) : showMessage(message)}" class="message-content">
            <header>
              <p class="recipient-name">{{isOutbox ? recipientName(message) : senderName(message)}}</p>
              <div class="subject-wrapper">
                <rp-sprite class="message-icon important" name="warning_rounded" v-if="message.important" />
                <p class="message-subject">{{message.subject}}</p>
              </div>
              <div class="message-date">{{message.created_at | dateFormat}}</div>
            </header>

            <main class="message-body-preview">
              <p>{{message.body}}</p>
              <rp-button
                class="read-message-btn"
                type="secondary"
                @click="showMessage(message)">
                Read Message
              </rp-button>
            </main>
          </div>
        </li>
      </ul>

      <rp-pagination :pageInfo="pageInfo" @getData="getData"/>
    </div>

    <rp-overlay @closeOverlay="hideMessage" :active="overlayActive">
      <div v-if="selectedMessage" class="card">
        <div @click="hideMessage" class="close-button">
          <rp-sprite class="message-icon" name="close" />
        </div>

        <header>
          <p class="message-subject">{{selectedMessage.subject}}</p>
        </header>

        <main class="message-body">
          <p v-html="selectedMessage.body"></p>
        </main>

        <footer>
          <div class="buttons-wrapper">
            <button class="cancel-btn" @click="hideMessage">Cancel</button>
            <div v-if="!isOutbox">
              <button v-if="messageArchived" class="archive-btn" @click="unarchiveMessage">Unarchive</button>
              <button v-else class="archive-btn" @click="archiveMessage">Archive</button>
            </div>
          </div>
          <button class="delete-message" @click="showDeleteAlert">Delete Message</button>
        </footer>
      </div>
    </rp-overlay>

    <rp-overlay :active="deleteActive">
      <rp-delete-confirm
        :messageText="messageDeleteText"
        :hideDeleteComponent="hideDeleteAlert"
        :confirm="deleteMessage"
        :cancel="hideDeleteAlert"
        icon="warning_rounded" />
    </rp-overlay>
  </section>
</template>

<script>
  import Pagination from './Pagination'
  import Overlay from './Overlay'
  import Sprite from './Sprite'
  import Alert from './Alert'
  import DeleteConfirm from './DeleteConfirm'
  import Button from './inputs/Button'

  export default {
    name: 'Inbox',

    components: {
      'rp-pagination': Pagination,
      'rp-overlay': Overlay,
      'rp-sprite': Sprite,
      'rp-button': Button,
      'rp-alert': Alert,
      'rp-delete-confirm': DeleteConfirm
    },

    props: {
      name: String,
      messageType: {
        type: String,
        default: 'inbox'
      },
      results: Function,
      authUser: Object,
      pages: Number,
      headings: Array,
      mob: Boolean,
      filter: {
        default: '',
        type: String,
      },
      overlayActive: false,
      selectedMessage: {}
    },

    data() {
      return {
        sortBy: '',
        sortOptions: [
          { label: 'Newest', direction: 'DESC'},
          { label: 'Oldest', direction: 'ASC'}
        ],
        rows: [],
        pageInfo: {
          page_number: 1,
          page_size: 10
        },
        archiveActive: false,
        deleteActive: false,
        archivedMessage: 'Message archived!',
        messageDeleteText: 'Do you really wish to delete this message?',
        messageArchiveText: 'Do you really wish to archive this message?'
      }
    },

    computed: {
      noData() {
        return !this.rows.length
      },

      messageArchived() {
        return !!this.selectedMessage.archived_at
      },

      senderName() {
        return function(message) {
          let profile = message.sender.profile
          return `${profile.first_name} ${profile.last_name}`
        }
      },

      recipientName() {
        return function(message) {
          let profile = message.recipient.profile
          return `${profile.first_name} ${profile.last_name}`
        }
      },

      senderInitial() {
        return function(message) {
          return message.sender.profile.first_name.charAt(0)
        }
      },

      recipientInitial() {
        return function(message) {
          return message.recipient.profile.first_name.charAt(0)
        }
      },

      messageParams() {
        return {
          message: {
            read_at: this.selectedMessage.read_at,
            important: this.selectedMessage.important,
            id: this.selectedMessage.id,
            body: this.selectedMessage.body,
            archived_at: this.selectedMessage.archived_at
          }
        }
      },

      isOutbox() {
        return this.messageType === 'outbox'
      }
    },

    filters: {
      dateFormat(val) {
        const date = new Date(val).toLocaleString()
        return date
      }
    },

    methods: {
      getData(p) {
        this.results(p, this.userId, this.params).then(({data, page}) => {
          this.rows = data
          this.pageInfo = page
        })
      },

      updateSortParam() {
        this.$emit('updateSortBy', this.sortBy)
      },

      mobMessage(msg) {
        this.$router.push({name: 'message', params: {message: msg}})
      },

      showMessage(message) {
        this.$emit('openMessage', message)
      },

      hideMessage() {
        this.$emit('closeMessage')
      },

      deleteMessage() {
        this.hideDeleteAlert()
        this.$emit('deleteMessage', this.selectedMessage.id)
      },

      archiveMessage() {
        this.selectedMessage.archived_at = new Date().toISOString()
        this.$emit('archiveMessage', this.selectedMessage.id, this.messageParams)
      },

      unarchiveMessage() {
        this.selectedMessage.archived_at = null
        this.$emit('archiveMessage', this.selectedMessage.id, this.messageParams)
      },

      showDeleteAlert() {
        this.deleteActive = true
      },

      hideDeleteAlert() {
        this.deleteActive = false
      },

      showArchiveAlert() {
        this.archiveActive = true
      },

      hideArchiveAlert() {
        this.archiveActive = false
      }
    },

    mounted() {
      this.getData(this.pageInfo)
    },

    watch: {
      $route() {
        this.getData(this.pageInfo)
      },

      sortBy() {
        this.getData(this.pageInfo)
      }
    }
  }
</script>

<style lang="sass">
  @import '../../styles/base/variables.sass'
  @import '../../styles/base/themes'
  @import '../../styles/helpers/mixins'

  .theme-mobile
    .message-body-preview
      display: none

    .recipient-name
      margin: 0
      font-weight: bold

  .messages
    padding-left: 0
    padding-top: 30px
    color: $color-body-bg

  .message-icon
    width: 20px
    height: 20px

    &.important
      fill: $color-secondary
      margin-right: 5px

  .messages-header
    display: flex
    justify-content: space-between
    align-items: center
    width: 100%
    border-bottom: 1px solid $color-border

    .theme-mobile &
      display: none

    select
      background: #fff
      min-width: 150px
      border: 1px solid $color-border
      border-radius: 0px
      padding: 0 5px
      -webkit-border-radius: 0px
      -webkit-appearance: none
      height: 30px
      line-height: 30px

      &:focus
        outline: none

  .subject-wrapper
    display: flex

  .message-subject
    font-size: 14px
    margin: 0

  .message-date
    font-size: 12px
    margin: 0

  .message
    list-style: none
    margin-bottom: 80px

    .theme-web &
      @include media($media-medium)
        display: flex
        flex-wrap: nowrap
        align-items: flex-start
        margin-bottom: 50px

    .theme-mobile &
      display: flex
      flex-wrap: nowrap
      align-items: flex-start
      margin-bottom: 30px

  .recipient-container, .actions
    .theme-web &
      @include media($media-medium)
        flex-basis: percentage(1/4)

  .actions
    &.desktop
      display: none

      .theme-mobile &
        display: none

      @include media($media-medium)
        display: block

    &.mobile
      position: absolute
      right: 0
      top: 0

      @include media($media-medium)
        display: none

  .recipient-container
    display: flex
    flex-direction: column

    .unread-label
      align-self: flex-end
      margin-top: 10px
      color: $color-secondary

  .recipient
    .theme-web &
      border-bottom: 1px solid rgba(0, 0, 0, 0.15)
      padding-bottom: 15px
      display: flex
      align-items: center
      position: relative
      color: $color-text-invert
      background-color: $color-primary
      border-radius: 5px

    .recipient-details
      p
        margin: 0

    .recipient-name
      font-size: 14px
      font-weight: bold

      .theme-mobile &
        display: none

    .role-title
      font-size: 10px
      margin: 0
      text-transform: uppercase

    .avatar
      width: 50px
      height: 50px
      background: $color-avatar
      margin-right: 10px
      color: #fff
      font-size: 16px
      position: relative
      border-radius: 50%

      .initial
        position: absolute
        top: 50%
        left: 50%
        transform: translate(-50%, -50%)

  .message-content
    .theme-mobile &
      width: 100%
      cursor: pointer

    .theme-web &
      border-radius: 3px
      padding: 25px
      margin: 25px 0
      background: $color-panel-bg
      box-shadow: 0 0 5px 0px $color-shadow

      @include media($media-medium)
        display: flex
        flex-direction: column
        flex-basis: 50%
        margin: 0 20px

        header
          align-content: center
          display: flex
          margin-bottom: 10px
          justify-content: space-between

      .recipient-name
        display: none

  .card
    background: $color-panel-bg
    box-shadow: 0 0 5px 0px $color-shadow
    width: 100%
    min-width: 300px
    margin: auto
    padding: 30px
    border-radius: 8px
    position: relative
    max-width: 90%

    .close-button
      position: absolute
      top: 10px
      right: 10px
      opacity: 0.75
      cursor: pointer

    .cancel-btn,
    .archive-btn,
    .delete-message
      cursor: pointer

      &:focus
        outline: none

    .cancel-btn, .archive-btn
      border: none
      background: none
      padding: 10px
      border: 1px solid rgba(0, 0, 0, 0.25)
      border-radius: 3px
      min-width: 130px
      text-transform: uppercase
      color: $color-text

    .delete-message
      color: $color-secondary
      background: none
      border: none
      padding: 10px
      text-transform: uppercase
      font-weight: bold
      display: block
      margin: 20px auto

      @include media($media-small)
        margin: 0

    footer
      margin-left: -30px
      width: calc(100% + 60px)
      border-top: 1px solid rgba(0, 0, 0, 0.15)
      padding: 30px 30px 0
      text-align: center

      .buttons-wrapper
        display: flex

      @include media($media-small)
        display: flex
        justify-content: space-between

    .message-subject
      border-bottom: 1px solid rgba(0, 0, 0, 0.2)
      padding-bottom: 10px
      font-weight: bold
      font-size: 14px
      margin-bottom: 30px

    .archive-btn
      margin-left: 5px

    .message-body
      margin: 30px 0
      overflow: auto
      -webkit-overflow-scrolling: touch

      .theme-web &
        max-height: 50vh

      .theme-mobile &
        height: 67vh

  .messages
    + .pagination
      .pages
        .change-btn
          .sprite
            fill: $color-text-invert
        .page-items
          .desktop-pagination
            &.is-active
              color: $color-text-invert
      .per-page
        select
          color: $color-text-invert
          background-color: $color-body-bg
</style>



// WEBPACK FOOTER //
// ../raypro_ui/src/components/ui/Messages.vue